import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { freight } from "../lib/freightApi.js";

/**
 * A carrier's public driver application: freight.alfredvoice.com/apply/<token>. No sign-in.
 *
 * ⛔ THE CONSENT WORDS COME FROM THE SERVER, and are the ones stored with the applicant's consent
 * (recruitingLib.applicantConsentText). Writing them here as well would be two copies that can
 * drift: the screen promising one thing and the record holding another.
 *
 * ⛔ NO DATE OF BIRTH, SSN, LICENCE NUMBER OR DRIVING RECORD. Not asked, not accepted. A recruiter
 * collects what the regulations require, with the driver, outside this app.
 */

const STATES = ("AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC " +
  "ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY").split(" ");
const CDLS = [["A", "Class A"], ["B", "Class B"], ["C", "Class C"], ["CLP", "Learner's permit"], ["none", "No CDL yet"]];
const ENDORSE = [["H", "Hazmat"], ["N", "Tanker"], ["T", "Doubles/triples"], ["X", "Tanker + hazmat"]];
const EQUIP = ["Dry van", "Reefer", "Flatbed", "Tanker", "Intermodal", "Car hauler"];

const C = { ink: "#111", ink2: "#555", ink3: "#888", hair: "#DCDCDC", wash: "#F5F5F2" };
const sans = `-apple-system, BlinkMacSystemFont, "Segoe UI", Inter, system-ui, sans-serif`;
const field = { font: "inherit", fontSize: 16, color: C.ink, border: `1px solid ${C.hair}`, borderRadius: 8, padding: "11px 12px", width: "100%", boxSizing: "border-box", background: "#fff" };

// `initialInfo` lets the render test draw the loaded form; the page itself always fetches.
export default function Apply({ initialInfo = null }) {
  const { token } = useParams();
  const [info, setInfo] = useState(initialInfo);
  const [gone, setGone] = useState(false);
  const [f, setF] = useState({ name: "", phone: "", email: "", city: "", state: "", cdlClass: "", endorsements: [], experienceYears: "", equipment: [], consent: false, website: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    freight.getApplication(token).then(setInfo).catch(() => setGone(true));
  }, [token]);

  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const toggle = (k, v) => setF((x) => ({ ...x, [k]: x[k].includes(v) ? x[k].filter((y) => y !== v) : [...x[k], v] }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try { setDone(await freight.submitApplication(token, f)); }
    catch (ex) {
      // A link that closed after this page loaded (the carrier paused): say so, rather than "try again" forever.
      if (ex?.status === 404) { setGone(true); return; }
      setErr({ field: ex?.body?.field || null, text: ex?.body?.message || "That did not go through. Please try again." });
    }
    finally { setBusy(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: C.wash, fontFamily: sans, color: C.ink, padding: "28px 16px 48px" }}>
      <div style={{ maxWidth: 520, margin: "0 auto", background: "#fff", border: `1px solid ${C.hair}`, borderRadius: 12, padding: "24px 20px" }}>
        {gone && <p style={{ margin: 0, fontSize: 15 }}>This application link is no longer active. Please contact the company directly.</p>}
        {!gone && !info && <p style={{ margin: 0, color: C.ink3 }}>Loading…</p>}
        {info && done && (
          <div>
            <h1 style={{ fontSize: 22, margin: "0 0 10px", letterSpacing: -0.4 }}>Thanks, {f.name.split(" ")[0] || "driver"}.</h1>
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.55, color: C.ink2 }}>
              {done.willCall
                ? `${info.carrierName}'s recruiting assistant, Alfred, will call you ${done.when === "later" ? "after 8am your time" : "within a few minutes"}. Alfred is an AI; the call may be recorded.`
                : `${info.carrierName}'s recruiting team has your application.`}
            </p>
          </div>
        )}
        {info && !done && !gone && (
          <form onSubmit={submit} style={{ display: "grid", gap: 16 }}>
            <div>
              <div style={{ fontSize: 12, letterSpacing: 1, textTransform: "uppercase", color: C.ink3, fontWeight: 600 }}>Driver application</div>
              <h1 style={{ fontSize: 24, margin: "6px 0 0", letterSpacing: -0.5 }}>Drive with {info.carrierName}</h1>
              <p style={{ margin: "6px 0 0", fontSize: 14, color: C.ink2, lineHeight: 1.5 }}>Two minutes. We'll take it from there.</p>
            </div>
            {/* The hidden field: off-screen, skipped by keyboards and password managers. A person never fills it. */}
            <input name="website" tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set("website")(e.target.value)}
              aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }} />
            <L label="Full name" error={err?.field === "name" && err.text}><input style={field} value={f.name} onChange={(e) => set("name")(e.target.value)} autoComplete="name" required /></L>
            <L label="Mobile phone" error={err?.field === "phone" && err.text}><input style={field} type="tel" value={f.phone} onChange={(e) => set("phone")(e.target.value)} autoComplete="tel" required /></L>
            <L label="Email (optional)" error={err?.field === "email" && err.text}><input style={field} type="email" value={f.email} onChange={(e) => set("email")(e.target.value)} autoComplete="email" /></L>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: 10 }}>
              <L label="City"><input style={field} value={f.city} onChange={(e) => set("city")(e.target.value)} autoComplete="address-level2" /></L>
              <L label="State" error={err?.field === "state" && err.text}>
                <select style={field} value={f.state} onChange={(e) => set("state")(e.target.value)} required>
                  <option value="">—</option>{STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  <option value="other">Outside the US</option>
                </select>
              </L>
            </div>
            <Group label="CDL">
              {CDLS.map(([v, label]) => <Pick key={v} type="radio" checked={f.cdlClass === v} onChange={() => set("cdlClass")(v)} label={label} />)}
            </Group>
            <Group label="Endorsements">
              {ENDORSE.map(([v, label]) => <Pick key={v} checked={f.endorsements.includes(v)} onChange={() => toggle("endorsements", v)} label={label} />)}
            </Group>
            <L label="Years of driving experience" error={err?.field === "experienceYears" && err.text}>
              <input style={{ ...field, maxWidth: 140 }} type="number" min="0" max="60" step="0.5" value={f.experienceYears} onChange={(e) => set("experienceYears")(e.target.value)} />
            </L>
            <Group label="Equipment you've run">
              {EQUIP.map((v) => <Pick key={v} checked={f.equipment.includes(v)} onChange={() => toggle("equipment", v)} label={v} />)}
            </Group>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13.5, lineHeight: 1.5, color: C.ink2 }}>
              <input type="checkbox" checked={f.consent} onChange={(e) => set("consent")(e.target.checked)} style={{ marginTop: 3, width: 18, height: 18 }} />
              <span>{info.consentText}</span>
            </label>
            {err && !err.field && <div style={{ fontSize: 14, color: C.ink }}>{err.text}</div>}
            <button type="submit" disabled={busy} style={{ font: "inherit", fontSize: 16, fontWeight: 600, padding: "13px 16px", borderRadius: 9, border: "none", background: C.ink, color: "#fff", cursor: busy ? "default" : "pointer", opacity: busy ? 0.5 : 1 }}>
              {busy ? "Sending…" : "Send my application"}
            </button>
            <p style={{ margin: 0, fontSize: 12, color: C.ink3, lineHeight: 1.5 }}>
              We never ask for your date of birth, Social Security number or licence number here. A recruiter goes over anything else with you directly.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function L({ label, error, children }) {
  return (
    <label style={{ display: "grid", gap: 6, fontSize: 13.5, color: C.ink2, minWidth: 0 }}>
      {label}{children}
      {error && <span style={{ fontSize: 13, color: C.ink }}>{error}</span>}
    </label>
  );
}
function Group({ label, children }) {
  return (
    <fieldset style={{ border: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
      <legend style={{ fontSize: 13.5, color: C.ink2, marginBottom: 6, padding: 0 }}>{label}</legend>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{children}</div>
    </fieldset>
  );
}
function Pick({ type = "checkbox", checked, onChange, label }) {
  return (
    <label style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 14.5, border: `1px solid ${checked ? C.ink : C.hair}`, borderRadius: 999, padding: "8px 13px", cursor: "pointer", background: checked ? C.wash : "#fff" }}>
      <input type={type} checked={checked} onChange={onChange} style={{ margin: 0 }} />{label}
    </label>
  );
}
